import type { MinionsDay, MinionsPayload, MinionsWeek } from './report';

/*
 * Two fallbacks for when the live report is not available:
 *
 *   MN_PREVIEW — sample figures shaped like the design's mid-campaign snapshot
 *     (Thursday 26 Nov, week 4 running). DEV ONLY, so the page can be reviewed
 *     against the design.
 *   MN_EMPTY   — the same structure with nothing measured. What a DEPLOYED build
 *     shows: a client is never served invented figures.
 *
 * The design's sample blobs are not readable outside the design, so these are
 * illustrative values in the same shape; they add up (daily games = weekly
 * games = campaign games, weekly new players = unique players, buckets = unique
 * players) so the page's cross-checks hold.
 */

const GO_LIVE = '2026-11-03'; // a Tuesday
const END = '2026-12-06';
const DAY_MS = 86_400_000;
const iso = (offset: number) => new Date(Date.parse(`${GO_LIVE}T00:00:00Z`) + offset * DAY_MS).toISOString().slice(0, 10);
const TOTAL_DAYS = Math.round((Date.parse(`${END}T00:00:00Z`) - Date.parse(`${GO_LIVE}T00:00:00Z`)) / DAY_MS) + 1;
const TOTAL_WEEKS = Math.ceil(TOTAL_DAYS / 7);

const CAMPAIGN = {
  name: 'Système U — Minions — Pinball',
  start: GO_LIVE,
  end: END,
  timezone: 'Europe/Paris',
  totalWeeks: TOTAL_WEEKS,
  dailyGames: 1,
  inviteCap: 10,
};

// date, unique visitors, active players, games played — 3 → 26 Nov (26 Nov partial).
const DAYS: Array<[string, number, number, number]> = [
  ['2026-11-03', 58000, 34800, 47328],
  ['2026-11-04', 58371, 36394, 47896],
  ['2026-11-05', 53792, 33847, 42261],
  ['2026-11-06', 50660, 31046, 39008],
  ['2026-11-07', 58163, 34126, 45323],
  ['2026-11-08', 56974, 32514, 44173],
  ['2026-11-09', 46382, 26754, 34867],
  ['2026-11-10', 45049, 27052, 33629],
  ['2026-11-11', 46249, 28851, 36535],
  ['2026-11-12', 43969, 27661, 37034],
  ['2026-11-13', 40286, 24670, 33414],
  ['2026-11-14', 45152, 26471, 34155],
  ['2026-11-15', 45728, 26091, 32362],
  ['2026-11-16', 37837, 21837, 27904],
  ['2026-11-17', 35356, 21249, 28639],
  ['2026-11-18', 36120, 22543, 30382],
  ['2026-11-19', 35682, 22443, 28677],
  ['2026-11-20', 32585, 19939, 24731],
  ['2026-11-21', 35121, 20575, 26548],
  ['2026-11-22', 36100, 20594, 27894],
  ['2026-11-23', 30905, 17847, 23894],
  ['2026-11-24', 28230, 16981, 21503],
  ['2026-11-25', 28024, 17498, 21753],
  ['2026-11-26', 15693, 9868, 12861],
];

// unique players, new players, login missions, invites sent, hours in the app — weeks 1–4 (week 4 running).
const WEEKS: Array<[number, number, number, number, number]> = [
  [98412, 98412, 4128, 9842, 21340],
  [84260, 41980, 14903, 6115, 16120],
  [70115, 22354, 9617, 4377, 12480],
  [38906, 6138, 2214, 1263, 3710],
];

const BUCKETS: Array<[string, number, number | null, number]> = [
  ['1', 1, 1, 61230],
  ['2-3', 2, 3, 47885],
  ['4-7', 4, 7, 33512],
  ['8-14', 8, 14, 19764],
  ['15+', 15, null, 6493],
];

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const round1 = (n: number) => Math.round(n * 10) / 10;

function previewDays(): MinionsDay[] {
  const byDate = new Map(DAYS.map(([date, visitors, activePlayers, plays]) => [date, { visitors, activePlayers, plays }]));
  return Array.from({ length: TOTAL_DAYS }, (_v, i) => {
    const date = iso(i);
    const d = byDate.get(date);
    return { date, visitors: d?.visitors ?? null, activePlayers: d?.activePlayers ?? null, plays: d?.plays ?? null };
  });
}

function weekShell(i: number) {
  const start = iso(i * 7);
  const end = iso(Math.min(TOTAL_DAYS - 1, i * 7 + 6));
  return { week: i + 1, start, end };
}

const PLANNED = { uniquePlayers: null, newPlayers: null, plays: null, tickets: null, loginMissions: null, invitesSent: null, timeSec: null };

function previewWeeks(days: MinionsDay[]): MinionsWeek[] {
  return Array.from({ length: TOTAL_WEEKS }, (_v, i) => {
    const shell = weekShell(i);
    const w = WEEKS[i];
    if (!w) return { ...shell, status: 'planned', ...PLANNED };
    const plays = sum(days.filter((d) => d.date >= shell.start && d.date <= shell.end).map((d) => d.plays ?? 0));
    return {
      ...shell,
      status: i === WEEKS.length - 1 ? 'live' : 'done',
      uniquePlayers: w[0],
      newPlayers: w[1],
      plays,
      tickets: plays,
      loginMissions: w[2],
      invitesSent: w[3],
      timeSec: w[4] * 3600,
    };
  });
}

const days = previewDays();
const weeks = previewWeeks(days);
const measured = DAYS.map((d) => d[2]);
const plays = sum(DAYS.map((d) => d[3]));
const uniquePlayers = sum(WEEKS.map((w) => w[1]));
const loginMissions = sum(WEEKS.map((w) => w[2]));
const lastFullWeek = DAYS.slice(14, 21).map((d) => d[2]);

export const MN_PREVIEW: MinionsPayload = {
  campaign: { ...CAMPAIGN, live: true, currentWeek: WEEKS.length },
  totals: {
    uniqueVisitors: 236540,
    reachedOptIn: 201370,
    optIns: 181215,
    uniquePlayers,
    plays,
    tickets: plays,
    totalTimeSec: sum(WEEKS.map((w) => w[4] * 3600)),
    sessions: 1318640,
    dauAvg: round1(sum(measured) / measured.length),
    dauLast7: round1(sum(measured.slice(-7)) / 7),
    wauLastFullWeek: WEEKS[2][0],
    dauLastFullWeek: round1(sum(lastFullWeek) / 7),
    d1: { cohort: 166170, returned: 49520 },
    d7: { cohort: 151402, returned: 27253 },
    recurringPlayers: 71040,
    loginMission: { completed: loginMissions, extraGames: loginMissions },
    invites: { sent: sum(WEEKS.map((w) => w[3])), inviters: 13416, inviteesPlayed: 8215, redeemed: 9358, extraGames: 9358, limitReached: 186 },
    consents: { any: 22418, email: 20734, sms: 7962 },
  },
  days,
  weeks,
  playsDistribution: BUCKETS.map(([bucket, min, max, players]) => ({ bucket, min, max, players })),
};

export const MN_EMPTY: MinionsPayload = {
  campaign: { ...CAMPAIGN, live: false, currentWeek: 0 },
  totals: {
    uniqueVisitors: 0,
    reachedOptIn: null,
    optIns: 0,
    uniquePlayers: 0,
    plays: 0,
    tickets: 0,
    totalTimeSec: null,
    sessions: null,
    dauAvg: null,
    dauLast7: null,
    wauLastFullWeek: null,
    dauLastFullWeek: null,
    d1: { cohort: 0, returned: 0 },
    d7: { cohort: 0, returned: 0 },
    recurringPlayers: 0,
    loginMission: { completed: 0, extraGames: 0 },
    invites: { sent: null, inviters: null, inviteesPlayed: 0, redeemed: 0, extraGames: 0, limitReached: 0 },
    consents: { any: 0, email: 0, sms: 0 },
  },
  days: Array.from({ length: TOTAL_DAYS }, (_v, i) => ({ date: iso(i), visitors: null, activePlayers: null, plays: null })),
  weeks: Array.from({ length: TOTAL_WEEKS }, (_v, i) => ({ ...weekShell(i), status: 'planned', ...PLANNED })),
  playsDistribution: BUCKETS.map(([bucket, min, max]) => ({ bucket, min, max, players: 0 })),
};
